import { useEffect } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";
import Box from "components/Box";
import Button from "components/Button";
import TextField from "components/TextField";
import TitleHeading from "components/TitleHeading";
import { useGetGrades, useSaveGrade } from "hooks/apis/grade";

type FormValues = {
  grades: Array<{
    id?: number;
    name: string;
    amount: number;
  }>;
};

const SettingsGradePage = () => {
  const methods = useForm<FormValues>();
  const { fields, remove, append } = useFieldArray({
    name: "grades",
    control: methods.control,
  });

  const { data: grades } = useGetGrades();
  const saveGradesMutation = useSaveGrade();

  useEffect(() => {
    if (grades) {
      methods.reset({
        grades: grades.map((grade) => ({
          id: grade.id,
          name: grade.name,
          amount: grade.amount,
        })),
      });
    }
  }, [grades]);

  const onAdd = () => {
    if (!grades) {
      return;
    }
    append({ name: "", amount: 0 });
  };

  const onSubmit = (form: FormValues) => {
    if (!grades) {
      return;
    }
    saveGradesMutation.mutate(form);
  };

  return (
    <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
      <TitleHeading
        className="mb-[0.625rem]"
        title="조교관리"
        description="조교 관련 정보 화면입니다."
      />
      <div className="max-w-[523px]">
        <Box className="mb-[0.313rem]">
          <h3 className="text-18 font-medium">등급관리</h3>
        </Box>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <table className="w-full">
              <thead>
                <tr className="h-14 text-16 text-white">
                  <th className="w-[5.875rem] rounded-l-md bg-secondary">
                    번호
                  </th>
                  <th className="bg-secondary">등급명</th>
                  <th className="bg-secondary">건당 급여</th>
                  <th className="w-[5.875rem] rounded-r-md bg-secondary">
                    삭제
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white text-16 font-medium border-b border-gray">
                {fields.map((field, index) => (
                  <tr key={field.id} className="h-14 border-t border-gray">
                    <td className="text-center text-secondary">{index + 1}</td>
                    <td>
                      <TextField
                        name={`grades[${index}].name`}
                        className="flex justify-center"
                        inputClassName="!h-9 w-[5.688rem]"
                        required
                      />
                    </td>
                    <td>
                      <TextField
                        name={`grades[${index}].amount`}
                        className="flex justify-center"
                        inputClassName="!h-9 w-[5.688rem]"
                        required
                        maxLength={4}
                        number
                        addComma
                      />
                    </td>
                    <td>
                      {![0, 1].includes(index) && (
                        <button
                          onClick={() => remove(index)}
                          type="button"
                          className="mx-auto flex justify-center items-center w-20 h-12"
                        >
                          <TrashIcon className="size-6" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2 mt-[1.875rem]">
              <Button
                disabled={!grades}
                outline
                onClick={onAdd}
                className="w-[10.688rem]"
              >
                등급추가
              </Button>
              <Button
                disabled={!grades}
                type="submit"
                className="w-[10.688rem]"
              >
                저장
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </main>
  );
};

export default SettingsGradePage;
